import moment from 'moment';

import { changeBigNumberFormat, getLanguageCode, intlCurrency } from '../../shared/helpers';

import navigatorSliderLeft from './images/navigator-slider-left.svg';
import navigatorSliderRight from './images/navigator-slider.svg';
import { TooltipFormatterContextObject } from 'highcharts';

const language = getLanguageCode();
const NAVIGATOR_SIZE = 100;

const mapDataTypeToSymbol: any = {
  percent: '%',
  permillion: '',
};

const twoDecimalCaseOption = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

interface HeightChartProps {
  elementClass?: string;
  customNavigatorSize?: number;
}

// This helper function is used to calculate the aspect ratio.
// In this case it only returns the available height for the chart
// To calculate with get the offset top from element above the chart and his height, the footer height, and the navigator size
// And remove it from the total height from the window
export const getHeightChart = ({ elementClass, customNavigatorSize }: HeightChartProps) => {
  const effectiveNavigatorSize = customNavigatorSize ?? NAVIGATOR_SIZE;
  if (elementClass) {
    const element: any = document.querySelector(elementClass);

    return element ? element.clientHeight - effectiveNavigatorSize : 0;
  }
  const editorTopRight: any = document.querySelector('.editor-top-bar-right');
  const footerElement: any = document.querySelector('.editor-bottom-bar-right');
  const offsetEditorTop = editorTopRight ? editorTopRight.offsetTop : 100;
  const footerElementHeight = footerElement ? footerElement.offsetHeight : 40;
  const editorTopHeight = editorTopRight ? editorTopRight.offsetHeight : 40;

  return window.innerHeight - offsetEditorTop - footerElementHeight - editorTopHeight - effectiveNavigatorSize;
};

export const highstockConfigNavigator = {
  navigator: {
    margin: 5,
    maskFill: '#96abba',
    height: 21,
    series: {
      fillOpacity: 0.0,
      lineWidth: 0,
    },
    xAxis: {
      tickPixelInterval: 200,
      gridLineWidth: 0,
      labels: {
        style: {
          color: 'none',
        },
      },
    },
    outlineColor: 'transparent',
    handles: {
      height: 21,
      width: 37,
      symbols: [`url(${navigatorSliderLeft})`, `url(${navigatorSliderRight})`],
    },
  },
};

export const highstockConfigxAxis = {
  xAxis: {
    gridLineWidth: 1,
    labels: {
      align: 'left',
      x: 0,
      y: -2,
    },
  },
};

export const highstockConfigyAxisSingle = (type: string) => ({
  yAxis: [
    {
      startOnTick: false,
      endOnTick: false,
      opposite: false,
      labels: {
        align: 'left',
        x: 0,
        y: -2,
        formatter: function (this: any) {
          // tslint:disable-next-line:no-invalid-this
          const value = this.value;
          const numericSymbols = ['', 'K', 'M', 'B', 'T'];
          const breaks = [5, 8, 11, 14, 17];
          const numberSize = Math.ceil(Math.log(Math.abs(value) + 1) / Math.LN10);
          const index = breaks.findIndex((element: number) => numberSize < element);
          let ret;
          if (index === 0) {
            if (type === 'permillion') {
              ret = `${value.toLocaleString(language, twoDecimalCaseOption)}`;
            } else {
              ret = `${value.toLocaleString(getLanguageCode())}${mapDataTypeToSymbol[type] ? mapDataTypeToSymbol[type] : ''}`;
            }
          } else {
            // tslint:disable-next-line:max-line-length
            ret = `${(value / Math.pow(10, breaks[index - 1] - 2)).toLocaleString(getLanguageCode())}${numericSymbols[index]}${
              mapDataTypeToSymbol[type] ? mapDataTypeToSymbol[type] : ''
            }`;
          }

          return ret;
        },
      },
    },
  ],
});

interface HighstockConfigChartParams {
  fatherClass?: string;
  isArea?: boolean;
  customNavigatorSize?: number;
}

export const highstockConfigChart = ({ fatherClass, isArea = false, customNavigatorSize }: HighstockConfigChartParams) => ({
  chart: {
    spacing: 0,
    ...(isArea && { type: 'area' }),
    height: getHeightChart({ elementClass: fatherClass, customNavigatorSize }),
    style: {
      fontFamily: '"Open Sans", "Verdana", sans-serif',
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontStretch: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      textAlign: 'center',
    },
    zoomType: 'x',
    panning: true,
    panKey: 'shift',
    resetZoomButton: {
      theme: {
        fill: 'white',
        stroke: 'silver',
        r: 0,
        states: {
          hover: {
            fill: '#41739D',
            style: {
              color: 'white',
            },
          },
        },
      },
      position: {
        verticalAlign: 'bottom',
      },
    },
  },
});

const getValueFormatted = (yValue: number, valueType: string, currency: any, type: string, symbol: string = '') => {
  if (valueType === 'integer') {
    return currency ? intlCurrency(currency, yValue) : yValue;
  } else {
    return `${yValue.toLocaleString(language, twoDecimalCaseOption)}${symbol}`;
  }
};

interface HighstockConfigTooltipParams {
  type: string;
  recordSet?: string;
  valueType?: string;
  currency?: string | null;
  isTotal?: boolean;
  isDateNotConverted?: boolean;
}

type TooltipEntry = {
  color: string;
  label: string;
  value: string;
  yValue: string;
};

function getTotal(toolTipContent: TooltipEntry[]) {
  return toolTipContent.reduce(
    (acc, current) => {
      acc.value += Number.parseFloat(current.value);

      return acc;
    },
    { ...toolTipContent[0], value: 0, label: 'Total' }
  );
}

export const highstockConfigTooltip = ({
  type,
  recordSet = 'coverage',
  valueType = 'decimal',
  currency = null,
  isTotal = false,
  isDateNotConverted = false,
}: HighstockConfigTooltipParams) => ({
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function (this: any) {
      let toolTipContent: any = [];
      this.points.forEach((entry: TooltipFormatterContextObject) => {
        const yValue = entry.y as number;
        const numericSymbols = ['', 'K', 'M', 'B', 'T'];
        const breaks = [5, 8, 11, 14, 17];
        const numberSize = Math.ceil(Math.log(Math.abs(yValue) + 1) / Math.LN10);
        const index = breaks.findIndex((element: number) => numberSize < element);
        let ret;

        if (index === 0) {
          ret = getValueFormatted(yValue, valueType, currency, type);
        } else {
          const val = yValue / Math.pow(10, breaks[index - 1] - 2);
          ret = getValueFormatted(val, valueType, currency, type, numericSymbols[index]);
        }
        const isAlphaPeriodGraph =
          recordSet !== 'coverage' && recordSet !== 'dataset-coverage' && recordSet !== 'valueRange' && recordSet !== 'distribution';
        const labelName = getLabelName(isAlphaPeriodGraph, entry);

        const tooltipArray = document.getElementsByClassName('highcharts-label-box');
        if (tooltipArray && tooltipArray.length > 0) {
          const value = tooltipArray[tooltipArray.length - 1];
          if (value) {
            value.setAttribute('stroke', entry.color as string);
          }
        }
        const newEntry = {
          color: entry.color,
          label: `${labelName} ${entry.series.name}`,
          value: `${ret}${mapDataTypeToSymbol[type] ? mapDataTypeToSymbol[type] : ''}`,
          yValue: yValue,
        };
        if (toolTipContent.length > 0 && toolTipContent[0].yValue > yValue) {
          toolTipContent = [...toolTipContent, ...[newEntry]];
        } else {
          toolTipContent = [...[newEntry], ...toolTipContent];
        }
      });

      if (isTotal) {
        toolTipContent = [...toolTipContent, getTotal(toolTipContent)];
      }

      const formattedDate = (isDateNotConverted ? moment(this.x) : moment.utc(this.x)).format('L');
      const xValue = recordSet !== 'distribution' ? formattedDate : this.x;

      return `${xValue}
              <table>
                ${toolTipContent
                  .map(
                    (element: any) =>
                      `<tr>
                    <td style="font-weight:bold; color:${element.color}">${element.label}:</td>
                    <td style="text-align: right">${element.value}</td>
                  </tr>`
                  )
                  .join('')}
              </table>`;
    },
  },
});

const getValueHighstockChart = (value: any, type: string | undefined) => {
  if (type === 'permillion') {
    return `${value.toLocaleString(language, twoDecimalCaseOption)}`;
  } else {
    return changeBigNumberFormat(value, 2);
  }
};

export const highstockChart = (type?: string) => ({
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function (this: any) {
      // tslint:disable-next-line:no-invalid-this
      let tooltipHtml = moment(this.x).format('L');
      tooltipHtml += '<table>';
      // tslint:disable-next-line:no-invalid-this
      this.points.forEach((entry: any) => {
        tooltipHtml += `<tr><td style="font-weight:bold; color:${entry.series.color}"> ${entry.series.name}:
          </td><td style="text-align: right">${getValueHighstockChart(entry.y, type)}</td></tr>`;
      });
      tooltipHtml += '</table>';

      return tooltipHtml;
    },
  },
});
const zoneNames = {
  is: 'IS',
  os: 'OS',
  train: 'Train',
  validation: 'Validation',
  test: 'Test',
};
function getLabelName(isAlphaPeriodGraph: boolean, entry: TooltipFormatterContextObject) {
  if (isAlphaPeriodGraph) {
    // @ts-ignore
    if (entry?.point?.zone?.className in zoneNames) {
      // @ts-ignore
      return zoneNames[entry?.point?.zone?.className];
    }
  }
  return '';
}

export const chartStyleVerdanaNormal = {
  fontFamily: '"Open Sans", "Verdana", sans-serif',
  fontWeight: 'normal',
  fontStyle: 'normal',
  fontStretch: 'normal',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  textAlign: 'center',
};

export const chartStyleVerdanafontWeight500Small = {
  fontFamily: '"Open Sans", "Verdana", sans-serif',
  fontWeight: 500,
  fontStyle: 'normal',
  fontStretch: 'normal',
  lineHeight: 'normal',
  letterSpacing: 'normal',
  textAlign: 'center',
  fontSize: '0.875rem',
};
